import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ArrowLeftIcon, ArrowRightIcon, XIcon } from 'lucide-react'

import { alpha, useTheme, Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import {
  Button,
  SPOTLIGHT_CORNER_RADIUS,
  SPOTLIGHT_GLOW_COLOR,
  SPOTLIGHT_GLOW_WIDTH,
  SPOTLIGHT_RING_COLOR,
  SPOTLIGHT_RING_WIDTH,
} from '@repo/ui'

import { playableSteps, type TourDef, type TourStep } from './tours'

/**
 * HOW a tour ended, because the three are different decisions:
 *
 *  - `complete` — reached the end (or took the terminal CTA). Recorded `completed`.
 *  - `skip`     — pressed "Skip tour". An explicit "I do not want this", so it is
 *                 recorded `dismissed` and never offered again.
 *  - `exit`     — Escape, or the card's close button. AMBIGUOUS, and in practice
 *                 much closer to "not now" than to "never": the tour stays
 *                 `in_progress` at the step reached, so it can be offered as
 *                 "Finish tour" and resumed where they stopped.
 *
 * `exit` did not exist until VRG-221 and everything non-complete collapsed to
 * `skip`. That made `tourProgressApi`'s own comment — "`in_progress` is deliberately
 * NOT terminal, someone who abandoned a tour halfway has not seen it" — describe an
 * intent the code could never deliver, because no path ever left a tour
 * `in_progress`. Splitting the reason is what makes that sentence true.
 */
export type TourEndReason = 'complete' | 'skip' | 'exit'

export type TourEventType = 'start' | 'step' | 'complete' | 'skip' | 'cta'

export interface TourEvent {
  tour: string
  step: string
  index: number
  total: number
  type: TourEventType
}

/** An edge-coordinate box — what `inflate` and `clipBoundsFor` speak in. */
interface Box {
  left: number
  top: number
  right: number
  bottom: number
}

interface Rect {
  top: number
  left: number
  width: number
  height: number
  /**
   * The region this rect's element is visible in — see `clipBoundsFor`. Carried on the
   * rect because `inflate` needs it and, by then, the element is long gone: measuring
   * happens on a timer and on scroll, the result is stored as plain numbers in state,
   * and re-querying the DOM at paint time would be measuring something else.
   */
  bounds?: Box
}

/**
 * px of breathing room around the highlighted element — a MAXIMUM, not a guarantee.
 * `inflate` gives it up, symmetrically, wherever the element has no room for it.
 */
const SPOTLIGHT_PAD = 8
/**
 * px the ring keeps clear of the viewport edge so its own border is actually drawn
 * ON screen.
 *
 * The ring's `outline` and its 4px glow render OUTSIDE the border box, so a ring
 * sitting exactly on the viewport boundary has that edge painted off-screen — it reads
 * as cut off, which is the reported defect. Numerically the box was inside the viewport
 * the whole time (measured 0 overflow on all four edges); it was the border that was
 * missing.
 *
 * A constant of this name existed before and was REMOVED, for a good reason that this
 * reinstatement must not undo: as a plain clamp it pushed the ring 6px INSIDE any
 * element flush against the window edge, so the sidebar's Help row was inset on the left
 * while overflowing on the right. It is now a bound the ring may not CROSS rather than a
 * position it must take — see `inflate`, which never moves an edge inside the element
 * itself. An element flush at x=0 still rings at x=0.
 */
const SPOTLIGHT_EDGE = 6
const CARD_WIDTH = 360
/** px between the spotlight and the step card. */
const CARD_GAP = 14
const VIEWPORT_MARGIN = 12
/**
 * Card height used for the FIRST render of a step only, before the card has
 * measured itself. Every subsequent layout uses the real height.
 *
 * It used to be the only height the positioning ever saw, and that is what put the
 * card on top of the thing it was describing: on `/settings/features` the real card
 * measured 242px against this 190, so a `top` placement computed from
 * `anchor.top − gap − 190` left the card's bottom 52px lower than the arithmetic
 * assumed — 46px of it inside the spotlight. A constant cannot know how long a step's
 * body copy is, so it is now a seed rather than an answer.
 */
const ESTIMATED_CARD_HEIGHT = 190
/** ms between re-measures while waiting for a late-mounting anchor. */
const MEASURE_INTERVAL_MS = 60
const MEASURE_MAX_TRIES = 15

/**
 * `document.querySelector` throws on a malformed selector, and an anchor id is
 * author-typed data. A tour must never take a page down, so every DOM lookup
 * goes through here: bad id, detached document, anything — returns null and the
 * step degrades to a centered card.
 */
function findAnchor(id: string | undefined): HTMLElement | null {
  if (!id) return null
  try {
    return document.querySelector<HTMLElement>(`[data-tour="${id}"]`)
  } catch {
    return null
  }
}

/**
 * The region an element is actually VISIBLE in: every clipping ancestor's rect,
 * intersected, and intersected with the viewport.
 *
 * This is what stops the spotlight bleeding out of the surface that contains its
 * target. An ancestor whose `overflow` is anything but `visible` genuinely cuts its
 * descendants off at its edges, so drawing a ring outside one highlights pixels the
 * element does not own — which is how the outro step's ring ended up 7px past the
 * right edge of the sidebar it was pointing into.
 *
 * The viewport is folded in here rather than applied as a separate minimum inset. It
 * used to be a `SPOTLIGHT_EDGE = 6` clamp, which had the side effect of pushing the
 * ring 6px INSIDE any element flush against the window edge — so the same sidebar row
 * was inset 6px on the left while overflowing 8px on the right, a halo that was wrong
 * in both directions at once. Bounding by the viewport keeps the ring on screen (the
 * only thing that constant was for) without ever cutting into the element itself.
 */
function clipBoundsFor(el: HTMLElement): Box {
  const bounds: Box = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
  let node = el.parentElement
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node)
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
      const r = node.getBoundingClientRect()
      bounds.left = Math.max(bounds.left, r.left)
      bounds.top = Math.max(bounds.top, r.top)
      bounds.right = Math.min(bounds.right, r.right)
      bounds.bottom = Math.min(bounds.bottom, r.bottom)
    }
    node = node.parentElement
  }
  return bounds
}

function rectOf(el: HTMLElement | null): Rect | null {
  if (!el) return null
  const r = el.getBoundingClientRect()
  // A zero-area anchor (display:none, an unmounted tab panel) is not something
  // we can point at — treat it as missing rather than drawing a ring on nothing.
  if (r.width === 0 && r.height === 0) return null
  return { top: r.top, left: r.left, width: r.width, height: r.height, bounds: clipBoundsFor(el) }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

type SidePlacement = 'top' | 'bottom' | 'left' | 'right'

/** Where a card of this size lands on a given side, BEFORE any viewport clamping. */
function placeOnSide(rect: Rect, side: SidePlacement, width: number, height: number) {
  switch (side) {
    case 'top':
      return { top: rect.top - CARD_GAP - height, left: rect.left + rect.width / 2 - width / 2 }
    case 'left':
      return { top: rect.top + rect.height / 2 - height / 2, left: rect.left - CARD_GAP - width }
    case 'right':
      return { top: rect.top + rect.height / 2 - height / 2, left: rect.left + rect.width + CARD_GAP }
    case 'bottom':
    default:
      return { top: rect.top + rect.height + CARD_GAP, left: rect.left + rect.width / 2 - width / 2 }
  }
}

const OPPOSITE_SIDE: Record<SidePlacement, SidePlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

/**
 * The sides to try, in order: what the step ASKED for, then its opposite, then the
 * other axis. The author's placement stays the preference — it encodes which side of
 * the anchor the surrounding page content is on — but it is a preference, not an
 * instruction, because whether it occludes depends on the viewport and on how long
 * the step's copy runs, neither of which the author can see.
 */
function candidateSides(preferred: SidePlacement): SidePlacement[] {
  const rest: SidePlacement[] = ['bottom', 'top', 'right', 'left']
  const opposite = OPPOSITE_SIDE[preferred]
  return [preferred, opposite, ...rest.filter(s => s !== preferred && s !== opposite)]
}

/** Overlapping area of a card rect and a spotlight box. 0 when they are disjoint. */
function overlapArea(card: { top: number; left: number }, width: number, height: number, spot: Box | null) {
  if (!spot) return 0
  const x = Math.min(card.left + width, spot.right) - Math.max(card.left, spot.left)
  const y = Math.min(card.top + height, spot.bottom) - Math.max(card.top, spot.top)
  return x > 0 && y > 0 ? x * y : 0
}

/**
 * Position the step card relative to the anchor rect.
 *
 * ## The rule, in priority order
 *
 * 1. **FULL VISIBILITY OF THE CARD IS A HARD CONSTRAINT.** Every candidate position is
 *    clamped inside the viewport and the card's height is capped to the viewport, so
 *    there is no position, viewport or step body that can put any part of the card off
 *    screen. A card the reader cannot read is worse than one overlapping the ring: the
 *    ring is a hint, the card is the content.
 * 2. **NOT OCCLUDING THE SPOTLIGHT is the objective it optimises within.** Among the
 *    positions that satisfy (1) — which is all of them — sides are tried in
 *    `candidateSides` order and the first that both fits the viewport along the axis it
 *    moved AND does not overlap the spotlight wins.
 * 3. **Smallest overlap when nothing is clean.** A short viewport or an anchor that
 *    nearly fills the screen leaves no clean side, so the card takes the least-occluding
 *    one rather than landing wherever the arithmetic pointed.
 *
 * The ordering was already what the code DID — the clamp kept the card on screen and the
 * search only ever chose between clamped positions — but nothing said so, and one path
 * did not honour it: a card taller than the viewport had its clamp range collapse to the
 * top margin and simply overflowed the bottom. Current step copy renders at 194–242px
 * against a 696px budget at 720, so it was latent rather than live; `maxHeight` plus an
 * internal scroll makes it unreachable instead of merely unlikely.
 *
 * Clamping on the CROSS axis does not disqualify a candidate: sliding a `bottom` card
 * sideways along a wide anchor keeps it below the anchor, which is the separation that
 * mattered. Clamping on the MAIN axis does, because that is exactly the move that
 * pushes a card back over the thing it is explaining.
 *
 * This replaces an unconditional `clamp(...)` into the viewport. That clamp guaranteed
 * the card was on screen and said nothing about what it covered, which is how the
 * `/settings/features` step ended up sitting on the trigger cards it was describing.
 *
 * With no rect (a centered step, or an anchor that isn't on the page) the card goes
 * dead center — except when the step asks for `top`/`bottom`, which pins it to that
 * viewport edge so a large centered surface it is describing (a modal) stays visible
 * behind it.
 */
function computeCardStyle(
  rect: Rect | null,
  placement: TourStep['placement'],
  measuredHeight: number,
  spot: Box | null,
  /**
   * Whether the step DECLARED an anchor — not whether one resolved.
   *
   * This is the distinction that decides the no-rect case, and the two situations look
   * identical from `rect === null` alone. A step with NO anchor is describing something the
   * whole viewport already shows (a modal), so pinning it to an edge deliberately keeps that
   * surface visible behind it. A step that NAMED an anchor and failed to find it is the
   * opposite: pinning it to the bottom edge points confidently at nothing, which is exactly
   * what "the tour went to the page and then stuck to the bottom" looks like.
   *
   * Centre that one instead — it reads as "here is some context", which is the truth.
   *
   * MERGE NOTE (VRG-436 x VRG-338): there are now THREE states reaching `rect === null`, and
   * this flag plus the caller's `spotlightable` gate separate all of them. A target that
   * RESOLVED but is too large to ring also passes `null` (see the call site), and it wants
   * the same centred treatment as an unresolved anchor — both declared one, so both get
   * `anchorDeclared: true`. Only a step with no anchor at all pins to an edge.
   */
  anchorDeclared: boolean
): React.CSSProperties {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const width = Math.min(CARD_WIDTH, vw - VIEWPORT_MARGIN * 2)
  /**
   * The card may never be taller than the viewport allows. `maxHeight` enforces it in
   * the DOM (the body scrolls inside the card instead of the card growing off screen),
   * and positioning uses the same capped figure so the two cannot disagree — a clamp
   * computed from an uncapped height is exactly how the card would have overflowed.
   */
  const maxHeight = Math.max(0, vh - VIEWPORT_MARGIN * 2)
  const boxed = { width, maxHeight }
  const centered: React.CSSProperties = {
    ...boxed,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  }

  if (placement === 'center') return centered

  if (!rect) {
    // Named an anchor, did not find it — say nothing rather than point at nothing.
    if (anchorDeclared) return centered
    if (placement === 'bottom') {
      return { ...boxed, left: '50%', bottom: VIEWPORT_MARGIN, transform: 'translateX(-50%)' }
    }
    if (placement === 'top') {
      return { ...boxed, left: '50%', top: VIEWPORT_MARGIN, transform: 'translateX(-50%)' }
    }
    return centered
  }

  // The height positioning reasons about, never the raw measurement — see `maxHeight`.
  const height = Math.min(measuredHeight, maxHeight)

  const clampPosition = (p: { top: number; left: number }) => ({
    top: clamp(p.top, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN)),
    left: clamp(p.left, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN)),
  })

  let fallback: { top: number; left: number; area: number } | null = null

  for (const side of candidateSides((placement ?? 'bottom') as SidePlacement)) {
    const raw = placeOnSide(rect, side, width, height)
    const vertical = side === 'top' || side === 'bottom'
    const mainAxisFits = vertical
      ? raw.top >= VIEWPORT_MARGIN && raw.top + height <= vh - VIEWPORT_MARGIN
      : raw.left >= VIEWPORT_MARGIN && raw.left + width <= vw - VIEWPORT_MARGIN

    const position = clampPosition(raw)
    const area = overlapArea(position, width, height, spot)

    if (mainAxisFits && area === 0) return { ...boxed, ...position }
    if (!fallback || area < fallback.area) fallback = { ...position, area }
  }

  return { ...boxed, top: fallback!.top, left: fallback!.left }
}

/**
 * Expand a rect by up to SPOTLIGHT_PAD, without leaving the region the element is
 * visible in.
 *
 * ## Why the pad is symmetric per axis
 *
 * The pad is reduced to the SMALLEST slack available on either side of an axis, then
 * applied to both sides. Intersecting an 8px-inflated rect with the bounds would be
 * simpler and would also stop the overflow, but it gives up evenness: an element 4px
 * below its container's top would get a 4px halo above and an 8px halo below, which
 * reads as a ring that has slipped rather than one that is centred on its target. A
 * spotlight's job is to say "this thing", so staying concentric with the thing matters
 * more than keeping the last few pixels of pad.
 *
 * Elements that fill their container therefore get NO pad, and that is the correct
 * answer rather than a compromise — the sidebar's Help row spans the sidebar's full
 * width, so there is nowhere for a horizontal halo to go. It used to take the 8px
 * anyway and paint it over the page beyond the sidebar's edge, which is what was
 * reported: a highlight wider than the thing it highlights.
 *
 * The final clamp handles the one case the pad arithmetic cannot: an element LARGER
 * than its clip bounds, or scrolled partly out of them, where the slack is negative.
 * The ring then stops at the bounds, which is where the element visibly stops.
 */
function inflate(base: Rect | null): Box | null {
  if (!base) return null
  const right = base.left + base.width
  const bottom = base.top + base.height
  const bounds = boundsOf(base)
  const usablePad = (before: number, after: number) =>
    Math.max(0, Math.min(SPOTLIGHT_PAD, before, after))
  const padX = usablePad(base.left - bounds.left, bounds.right - right)
  const padY = usablePad(base.top - bounds.top, bounds.bottom - bottom)
  /*
    Two bounds per edge, and the order matters.

    `Math.min(base.left, …)` is the one that keeps SPOTLIGHT_EDGE from repeating its old
    mistake: the ring may be pulled in as far as the element's own edge and no further,
    so an element flush against the window still rings flush. `Math.max(SPOTLIGHT_EDGE,
    …)` then keeps the ring's border on screen for every element that is NOT flush —
    which is the case that was rendering its outline off-viewport.
  */
  const nearEdge = (elementEdge: number, padded: number, safe: number, hard: number) =>
    Math.max(hard, Math.min(elementEdge, Math.max(safe, padded)))
  const farEdge = (elementEdge: number, padded: number, safe: number, hard: number) =>
    Math.min(hard, Math.max(elementEdge, Math.min(safe, padded)))
  return {
    left: nearEdge(base.left, base.left - padX, SPOTLIGHT_EDGE, bounds.left),
    top: nearEdge(base.top, base.top - padY, SPOTLIGHT_EDGE, bounds.top),
    right: farEdge(right, right + padX, window.innerWidth - SPOTLIGHT_EDGE, bounds.right),
    bottom: farEdge(bottom, bottom + padY, window.innerHeight - SPOTLIGHT_EDGE, bounds.bottom),
  }
}

/** The region this rect's element is visible in, defaulting to the whole viewport. */
function boundsOf(base: Rect): Box {
  return (
    base.bounds ?? { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
  )
}

/**
 * Whether this target can be RINGED at all.
 *
 * A spotlight means "this thing, and not that thing". A target that runs past the edge
 * of the screen has no "not that": the ring becomes a box around everything, which
 * carries no information, and the two edges that fall outside the viewport are painted
 * off-screen so it reads as cut off. That is the reported defect — on `/whats-new` the
 * anchored element measured 976 x 1749.5 in an 800px-tall viewport, and the ring drew
 * its left and right borders around the entire content area with top and bottom missing.
 *
 * THAT PARTICULAR CASE NO LONGER REPRODUCES, and the distinction matters when you next
 * read this. `/whats-new` was anchored at the feed, and the feed was the wrong target:
 * its height is a function of how many releases we have shipped, so no ring could ever
 * have been right there. The anchor moved to the first entry card, which is bounded and
 * is what the step's copy describes. This guard is not what fixed it — it is what made
 * the breakage legible instead of shipping a ring around the content area.
 *
 * So the guard stays, for the general case: any anchor can outgrow the viewport later
 * (a table that fills the page, a list that grows), and degrading honestly beats a ring
 * that lies about where a thing ends. But a step that HITS this guard is a smell, not a
 * resolution. The right response is usually to re-anchor at something bounded, and only
 * then to accept the flat dim.
 *
 * So a target that cannot be shown whole is not spotlighted at all: the step degrades to
 * the page-level presentation it already has for an anchorless step — flat dim, no ring,
 * centered card. Honest, and better-looking than the alternative, because the ring was
 * also what forced the card into a corner.
 *
 * The two rejected options, for the record:
 *  - CLAMP to the visible part ("the bit you can see") is what the code did already, and
 *    it produces the artifact above. Giving the clamped box a visible border is worse
 *    still: it draws a closed rectangle asserting a boundary the content does not have,
 *    i.e. it lies about where the thing ends.
 *  - SCROLL the target into view first cannot help — at 1749.5px against an 800px
 *    viewport there is no scroll offset at which it fits.
 *
 * A partial ring (brackets on the edges that are real, open where the content continues)
 * WOULD be honest and keeps the "look here" signal. It needs four separately positioned
 * edges instead of one outlined box and a new visual idiom, so it is out of scope here
 * rather than rejected on the merits.
 *
 * The test is per EDGE, not on size, and it asks ONE question of each: is this edge a
 * real boundary, or is it just where the screen stops?
 *
 *   - the ELEMENT's own edge  → real. Draw it, even at x=0. The sidebar's Help row is
 *     flush against the window and its ring is correct flush; that is where the row
 *     genuinely ends.
 *   - a CLIP CONTAINER's edge that is itself on screen → real. The settings sub-nav is
 *     952px of list inside a container ending at 776, and a ring at 776 sits on a
 *     boundary the user can see.
 *   - the VIEWPORT's edge, reached because the element runs past it → NOT real. This is
 *     the only failing case, and it is the reported one.
 *
 * Getting this split right is the whole reason the test is not a size comparison. A
 * plain "does it fit on screen" would have taken the ring off BOTH `/whats-new` (correct)
 * and the settings sub-nav (wrong), and an inset-based test would have taken it off the
 * sidebar Help row — the outro step of every tour in the app.
 */
function canSpotlight(base: Rect | null): boolean {
  if (!base) return false
  const bounds = boundsOf(base)
  const right = base.left + base.width
  const bottom = base.top + base.height
  const visible = {
    left: Math.max(base.left, bounds.left),
    top: Math.max(base.top, bounds.top),
    right: Math.min(right, bounds.right),
    bottom: Math.min(bottom, bounds.bottom),
  }
  // Float tolerance: these are getBoundingClientRect values, so an edge that IS the
  // element's own can differ in the last bit after the Math.min/max above.
  const isOwn = (a: number, b: number) => Math.abs(a - b) < 0.5
  return (
    (isOwn(visible.left, base.left) || visible.left > 0) &&
    (isOwn(visible.top, base.top) || visible.top > 0) &&
    (isOwn(visible.right, right) || visible.right < window.innerWidth) &&
    (isOwn(visible.bottom, bottom) || visible.bottom < window.innerHeight)
  )
}

export interface ProductTourProps {
  tour: TourDef
  open: boolean
  /** Fired when the tour ends, either way. */
  onEnd: (reason: TourEndReason) => void
  /** Analytics sink. */
  onEvent?: (event: TourEvent) => void
  /** Maps a terminal step's CTA to a side effect (open a modal, navigate). */
  onCta?: (step: TourStep) => void
  /** Runs a step's `onEnter` when it becomes active — opens the modal / switches
   *  the tab the step describes, so the spotlight lands on real UI. */
  onStepEnter?: (step: TourStep) => void
  /** Runs an `awaitAction` step's action when the user takes the "do it for me"
   *  button instead of clicking the spotlighted element themselves. */
  onAwaitAction?: (step: TourStep) => void
  /** Launch another tour from a terminal step's branch button. */
  onBranch?: (tourId: string) => void
  /** Step index to open at — for RESUMING a tour left part-way. Omitted or 0
   *  starts at the beginning. Clamped to the tour's real length on open. */
  startAtStep?: number
}

/**
 * The tour engine: a dimmed overlay with a spotlight cut around the current
 * step's anchor, plus a floating step card.
 *
 * Layering (mapped onto MUI's zIndex scale rather than magic numbers):
 *   drawer 1200  — the app sidebar
 *   DIM    1250  — this overlay's dim + spotlight ring: above the sidebar so it
 *                  can be spotlighted, below modals so a modal a step opens
 *                  stays readable rather than being greyed out
 *   modal  1300  — app modals
 *   CARD   1400  — the step card: above any modal a step opened, so the copy
 *                  explaining that modal is never buried under it
 *   tooltip 1500 — untouched
 *
 * Graceful degradation is the load-bearing property: an anchor that has been
 * renamed, removed, or is simply not on this route resolves to `null` and the
 * step falls back to a centered card with no ring. The tour keeps running and
 * the page keeps working. `scripts/check-tour-anchors.mjs` is what stops that
 * silent degradation from shipping.
 */
export const ProductTour = ({
  tour,
  open,
  onEnd,
  onEvent,
  onCta,
  onStepEnter,
  onAwaitAction,
  onBranch,
  startAtStep,
}: ProductTourProps) => {
  const theme = useTheme()
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  // Separate rect for the ring when step.spotlight differs from step.anchor —
  // the card can be positioned against a container while the ring calls out one
  // control inside it.
  const [spotRect, setSpotRect] = useState<Rect | null>(null)

  /**
   * The card's REAL height, measured from the rendered card.
   *
   * Positioning needs the height before the card exists, so the first render of a step
   * uses whatever height was last measured (seeded with `ESTIMATED_CARD_HEIGHT`) and
   * this corrects it. `useLayoutEffect` rather than an observer for two reasons: it runs
   * synchronously BEFORE paint, so the correction is never a visible jump, and it does
   * not depend on `ResizeObserver`/`requestAnimationFrame`, neither of which fires in
   * the Claude preview pane — a measurement that cannot be verified is not much of a
   * measurement.
   *
   * No dependency array on purpose: the height is a function of the step's copy AND the
   * viewport width, so anything that re-renders the card can change it. The `> 0.5`
   * guard is what stops that from looping — position does not affect height (the width
   * is fixed), so it settles in one extra pass.
   */
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [cardHeight, setCardHeight] = useState(ESTIMATED_CARD_HEIGHT)
  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    const measured = el.getBoundingClientRect().height
    if (measured > 0 && Math.abs(measured - cardHeight) > 0.5) setCardHeight(measured)
  })

  /**
   * `steps` is what PLAYS (content + the shared outro); `total` is what COUNTS
   * (content only). Keeping them separate is the whole mechanism behind "the outro
   * does not count": the engine walks `steps`, and every number a user sees —
   * the card counter, the event `total`, the library's bar — comes from `total`.
   */
  const steps = playableSteps(tour)
  const total = tour.steps.length
  /** Index of the appended outro. Equal to `total` because it sits just past the
   *  last content step. */
  const outroIndex = total
  const onOutro = index >= outroIndex

  /**
   * The step this open actually begins on — the saved position when resuming, 0
   * otherwise, clamped to the tour's real length.
   *
   * Computed once and used by BOTH the index reset and the `start` event, because
   * the two disagreeing is precisely the bug that shipped: the effect opened at the
   * resumed step while the event announced step 0, and the host writes progress
   * from the event.
   */
  const resolvedStart = clamp(startAtStep ?? 0, 0, Math.max(total - 1, 0))
  const step: TourStep | undefined = steps[index]

  /**
   * Open at `startAtStep` when resuming, otherwise at the beginning.
   *
   * Clamped, because the step count is code and the saved index is data: a tour
   * that loses steps in a later release must not open past its own end. Resolved
   * once per open — deliberately NOT reactive to `startAtStep` changing mid-tour,
   * since progress writes as the user advances and reacting to it would fight the
   * user's own navigation.
   */
  useEffect(() => {
    if (!open) return
    setIndex(resolvedStart)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tour.id])

  // One "start" event per open. `onEvent` is deliberately not a dependency —
  // this fires once per open, not on every identity change of the sink.
  useEffect(() => {
    if (!open) return
    // `resolvedStart`, NOT 0. The host maps a `start` event to `markStep`, so a
    // hard-coded 0 here wrote step 0 the instant a RESUMED tour opened — silently
    // rewinding the position it had just resumed from. Invisible in the UI (the
    // card was on the right step) and only caught by reading the row afterwards:
    // resume at 4, skip, and the database recorded step 0. That is the number
    // VRG-267's funnel would have been built on.
    onEvent?.({
      tour: tour.id,
      step: steps[resolvedStart]?.id ?? '',
      index: resolvedStart,
      total,
      type: 'start',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tour.id])

  /**
   * Re-read the anchor rects.
   *
   * `reveal` scrolls the anchor into view, and is passed ONLY when a step
   * activates — never from the scroll/resize listeners. Scrolling on every
   * re-measure means that the moment the user scrolls the anchor out of view the
   * handler yanks the page straight back, which reads as the overlay fighting
   * the mouse. (The upstream Vergence version does exactly that, and it is also
   * why this is not wired directly as the listener: an event handler receives
   * the Event as its first argument, which would make any boolean flag truthy.)
   */
  const measure = useCallback(
    (reveal = false) => {
      const current = steps[index]
      const el = findAnchor(current?.anchor)
      setRect(rectOf(el))
      setSpotRect(rectOf(findAnchor(current?.spotlight)))
      // `nearest` so an already-visible anchor isn't repositioned for no reason.
      if (reveal) el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    },
    [tour.steps, index]
  )

  // Run the step's onEnter the moment it activates, so the surface it describes
  // is present before we measure.
  useLayoutEffect(() => {
    if (open && step) onStepEnter?.(step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index])

  // Measure, retrying briefly. An onEnter action (a tab switch, a modal open)
  // mounts the anchored element a render or two later, so a one-shot measure
  // would miss it and fall back to a centered card. Poll until it resolves or
  // ~900ms elapses, then give up quietly.
  useLayoutEffect(() => {
    if (!open) return
    measure(true)
    const anchor = steps[index]?.anchor
    if (!anchor) return
    let tries = 0
    const id = window.setInterval(() => {
      tries += 1
      const found = findAnchor(anchor)
      measure(Boolean(found))
      if (found || tries >= MEASURE_MAX_TRIES) window.clearInterval(id)
    }, MEASURE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [open, index, measure, tour.steps])

  // Keep the spotlight pinned through scroll/resize. Capture-phase scroll so
  // scrolling inside a table or a drawer counts, not just the window.
  useEffect(() => {
    if (!open) return
    const remeasure = () => measure()
    window.addEventListener('resize', remeasure)
    window.addEventListener('scroll', remeasure, true)
    return () => {
      window.removeEventListener('resize', remeasure)
      window.removeEventListener('scroll', remeasure, true)
    }
  }, [open, measure])

  const goNext = useCallback(() => {
    // Advance through `steps` (content + outro), not `total` (content only). The
    // boundary was `total - 1` when they were the same array; leaving it there
    // after the split would complete the tour one step early and never show the
    // outro at all.
    if (index < steps.length - 1) {
      const next = index + 1
      setIndex(next)
      onEvent?.({ tour: tour.id, step: steps[next].id, index: next, total, type: 'step' })
    } else {
      onEvent?.({ tour: tour.id, step: step?.id ?? '', index, total, type: 'complete' })
      onEnd('complete')
    }
  }, [index, steps.length, total, tour, step, onEnd, onEvent])

  const goBack = useCallback(() => setIndex(i => Math.max(0, i - 1)), [])

  /** "Skip tour" — an explicit decline. Terminal; never offered again. */
  const doSkip = useCallback(() => {
    onEvent?.({ tour: tour.id, step: step?.id ?? '', index, total, type: 'skip' })
    onEnd('skip')
  }, [tour.id, step?.id, index, total, onEnd, onEvent])

  /**
   * Escape, or the card's close button — "not now", not "never".
   *
   * Emits the same `skip` ANALYTICS event as a real skip, deliberately: for funnel
   * purposes both are "left at step N", and inventing a second event type here
   * would fork the metric that VRG-267 is specified against. The difference is a
   * PERSISTENCE decision, not a measurement one, so it rides on the end reason
   * rather than on the event.
   */
  const doExit = useCallback(() => {
    onEvent?.({ tour: tour.id, step: step?.id ?? '', index, total, type: 'skip' })
    onEnd('exit')
  }, [tour.id, step?.id, index, total, onEnd, onEvent])

  const doCta = useCallback(() => {
    if (!step?.cta) return
    onEvent?.({ tour: tour.id, step: step.id, index, total, type: 'cta' })
    onCta?.(step)
    // Taking the CTA is a completion — the user did the thing.
    onEnd('complete')
  }, [step, tour.id, index, total, onCta, onEnd, onEvent])

  // awaitAction "do it for me": run the action on the user's behalf, then
  // advance — the same outcome as if they had clicked the real element.
  const doAwaitAction = useCallback(() => {
    if (!step?.awaitAction) return
    onEvent?.({ tour: tour.id, step: step.id, index, total, type: 'cta' })
    onAwaitAction?.(step)
    goNext()
  }, [step, tour.id, index, total, onAwaitAction, goNext, onEvent])

  // awaitAction: clicking the REAL spotlighted element advances the tour, and
  // its own handler fires too — so the user both learns the gesture and gets the
  // result. The click reaches it because the catcher opens a hole over the rect.
  // Re-binds when `rect` resolves so a late-mounting anchor still wires up.
  useEffect(() => {
    if (!open || !step?.awaitAction || !step.anchor) return
    const el = findAnchor(step.anchor)
    if (!el) return
    const handler = () => goNext()
    el.addEventListener('click', handler)
    return () => el.removeEventListener('click', handler)
  }, [open, step, rect, goNext])

  // Esc skips, →/Enter advance, ← goes back.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      // An awaitAction step leaves a real control clickable, and that control can
      // be a text field. Enter/arrows belong to whatever the user is typing in,
      // not to the tour. Esc still works — closing is always a valid escape.
      const el = e.target as HTMLElement | null
      const typing =
        el instanceof HTMLElement &&
        (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
      if (typing && e.key !== 'Escape') return
      if (e.key === 'Escape') {
        e.preventDefault()
        doExit()
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        goNext()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goBack()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, doExit, goNext, goBack])

  if (!open || !step || typeof document === 'undefined') return null

  const placement = step.placement ?? (rect ? 'bottom' : 'center')
  /**
   * Last step of what actually PLAYS — the outro, not the last content step.
   *
   * `total - 1` was right while `steps` and `tour.steps` were the same array. After
   * the outro was split out it made the final content step claim to be the end:
   * the button read "Done", `branches` and the terminal `cta` rendered a step early,
   * and Enter finished the tour before the outro was ever shown. Caught in
   * verification — the walkthrough reached "5 of 5" with a Done button while a step
   * still remained.
   */
  const isLast = index === steps.length - 1
  /**
   * Last step of what the AUTHOR wrote — distinct from `isLast` above, and both
   * are needed. `cta`/`branches` are gated on THIS, not on `isLast`.
   *
   * `TOUR_OUTRO_STEP` (the shared closing step every tour gets appended, per
   * `tours.ts`) carries neither `cta` nor `branches` — it's a fixed "Replay this
   * any time" card. So gating those two fields on `isLast` (true only for that
   * appended outro) makes them permanently unreachable on any tour that ends
   * normally, for every tour, always — not a per-tour authoring mistake. Found
   * during VRG-228: a step's `cta` rendered a plain "Next" button instead of its
   * label, on every one of the five tours that tried to use `cta`/`branches`.
   *
   * The two checks serve different questions and both stay: `isLast` answers
   * "is this the very end of the play-through" (drives the counter, the
   * Done/Next label, `goNext`'s completion branch) and correctly stays anchored
   * to the outro so THAT bug (fixed above) doesn't come back. `isLastContent`
   * answers "is this the author's final word before the outro" — the one place
   * a hand-off (`branches`) or a terminal action (`cta`) makes sense, since
   * taking either already ends the tour itself (`doCta` calls `onEnd('complete')`
   * directly; `handleBranch` in the provider switches tours outright) — neither
   * needs the outro to also render afterward.
   */
  const isLastContent = index === total - 1
  // An interactive step needs the spotlighted element to stay CLICKABLE, so the
  // catcher becomes four panels around the hole instead of one full-screen sheet.
  const isAwait = Boolean(step.awaitAction) && Boolean(rect)
  /**
   * The ring's target, or `null` when this target cannot be ringed without running off
   * the screen — see `canSpotlight`. A `null` here degrades the step to the page-level
   * presentation the engine already has for an anchorless step: flat dim, no ring, and
   * (below) a card positioned as if there were no rect, since there is now no ring for
   * it to avoid and no reason to jam it into a corner.
   */
  const ringTarget = spotRect ?? rect
  const spotlightable = canSpotlight(ringTarget)
  const spot = spotlightable ? inflate(ringTarget) : null
  // The click-through hole must expose BOTH the element the user interacts with
  // (`spotlight`) and the element that advances the tour (`anchor`) — when they
  // differ, exposing only one leaves the other under a catcher panel and the
  // step dead-ends. So the hole spans their union; the ring keeps using `spot`.
  // With no separate spotlight the union collapses to the anchor, unchanged.
  const holeBase: Rect | null =
    rect && spotRect
      ? {
          left: Math.min(rect.left, spotRect.left),
          top: Math.min(rect.top, spotRect.top),
          width:
            Math.max(rect.left + rect.width, spotRect.left + spotRect.width) -
            Math.min(rect.left, spotRect.left),
          height:
            Math.max(rect.top + rect.height, spotRect.top + spotRect.height) -
            Math.min(rect.top, spotRect.top),
          // UNION of the two elements' clip bounds, not their intersection. The hole is
          // what keeps a control clickable, so over-clipping it dead-ends the step —
          // and the two elements may legitimately live in different containers. The
          // ring keeps its own element's bounds, which is what the pad is measured
          // against.
          bounds:
            rect.bounds && spotRect.bounds
              ? {
                  left: Math.min(rect.bounds.left, spotRect.bounds.left),
                  top: Math.min(rect.bounds.top, spotRect.bounds.top),
                  right: Math.max(rect.bounds.right, spotRect.bounds.right),
                  bottom: Math.max(rect.bounds.bottom, spotRect.bounds.bottom),
                }
              : undefined,
        }
      : spotRect ?? rect
  /**
   * Deliberately NOT gated on `spotlightable`. The ring is decoration; the hole is what
   * keeps an `awaitAction` step's control clickable. An oversized target still has a
   * visible part the user can reach, so it keeps its hole even though it gets no ring —
   * dropping both would dead-end the step.
   */
  const hole = inflate(holeBase)

  // AFTER `spot`, because the card's position is now a function of where the spotlight
  // is — the whole point of the placement rule is that the card gets out of its way.
  // A target that could not be ringed passes `null`, so the card centers rather than
  // dodging a ring that is not drawn. `anchorDeclared` stays TRUE in that case — the step
  // did name an anchor, it just could not be shown whole — which is what routes it to the
  // centred treatment rather than the edge-pinned one reserved for anchorless steps.
  const cardStyle = computeCardStyle(
    spotlightable ? rect : null,
    placement,
    cardHeight,
    spot,
    Boolean(step?.anchor),
  )

  const dimZ = theme.zIndex.drawer + 50
  const cardZ = theme.zIndex.modal + 100
  const dimColour = alpha(theme.palette.common.black, 0.55)
  /*
    Read from `@repo/ui`'s spotlight token, NOT from `theme.palette.brand[600]`, even
    though in this app the two are the same value.

    They are the same here and are NOT the same in `apps/app`, whose palette replaces the
    whole `brand` ramp with one derived from the tenant's `primary_color`. The Studio
    token highlight draws this identical ring inside that app's preview, so the geometry
    and the colour have to come from somewhere theme-independent or the two treatments
    silently diverge per tenant. Sourcing both from one constant is what makes "the same
    spotlight" a fact rather than a resemblance. See `packages/ui/theme/spotlight.ts`.
  */
  const ring = SPOTLIGHT_RING_COLOR
  const catcherSx: SxProps<Theme> = { position: 'absolute', pointerEvents: 'auto' }

  const backgroundLayer = createPortal(
    <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: dimZ }}>
      {/* Click-catcher — swallows background interaction so a stray click can't
          fire an app action mid-tour. A normal step uses one full-screen sheet;
          an awaitAction step uses four panels AROUND the hole, leaving the real
          element clickable. */}
      {isAwait && hole ? (
        <>
          <Box aria-hidden sx={[catcherSx, { left: 0, right: 0, top: 0, height: hole.top }]} />
          <Box aria-hidden sx={[catcherSx, { left: 0, right: 0, bottom: 0, top: hole.bottom }]} />
          <Box
            aria-hidden
            sx={[catcherSx, { left: 0, top: hole.top, height: hole.bottom - hole.top, width: hole.left }]}
          />
          <Box
            aria-hidden
            sx={[catcherSx, { right: 0, top: hole.top, height: hole.bottom - hole.top, left: hole.right }]}
          />
        </>
      ) : (
        <Box aria-hidden sx={[catcherSx, { inset: 0 }]} />
      )}

      {/* The dim is painted by the spotlight's own huge outer shadow spread, so
          the anchor stays lit through the cutout. With no anchor there is
          nothing to cut out — fall back to a flat dim over everything. */}
      {spot ? (
        <Box
          sx={{
            position: 'absolute',
            pointerEvents: 'none',
            borderRadius: `${SPOTLIGHT_CORNER_RADIUS}px`,
            top: spot.top,
            left: spot.left,
            width: Math.max(0, spot.right - spot.left),
            height: Math.max(0, spot.bottom - spot.top),
            outline: `${SPOTLIGHT_RING_WIDTH}px solid ${ring}`,
            boxShadow: `0 0 0 9999px ${dimColour}, 0 0 0 ${SPOTLIGHT_GLOW_WIDTH}px ${SPOTLIGHT_GLOW_COLOR}`,
            transition: theme.transitions.create(['top', 'left', 'width', 'height'], {
              duration: theme.transitions.duration.shortest,
            }),
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: dimColour, pointerEvents: 'none' }} />
      )}
    </Box>,
    document.body
  )

  const cardLayer = createPortal(
    <Box
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Product tour: ${tour.label}`}
      sx={{
        position: 'fixed',
        pointerEvents: 'auto',
        zIndex: cardZ,
        bgcolor: 'background.default',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: theme.shadows[24],
        outline: 'none',
        // The partner to `maxHeight` in `computeCardStyle`. Capping the height without
        // this would hide the overflow instead of moving it — the buttons would be the
        // part that vanished, since they are last. A step long enough to need it should
        // scroll to its own controls rather than lose them.
        overflowY: 'auto',
      }}
      style={cardStyle}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ px: 4, pt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {/*
            No counter on the outro. It is not one of the tour's steps — it is the
            same closing card every tour ends with — so numbering it would both
            inflate the length ("6 of 6" for a five-step tour) and, worse, tell
            someone who has finished that they still have something left to learn.
            `total` is content-only, so rendering the counter here would read
            "6 of 5", which is the arithmetic saying the same thing.
          */}
          {!onOutro && (
            <Typography variant="body4" color="text.secondary">
              {index + 1} of {total}
            </Typography>
          )}
          {/*
            "Start over" — shown on any step past the first, however you got there.

            It was originally gated on RESUMED tours only, reasoning that on a linear
            walk the earlier steps are fresh and Back is adjacent, so the control
            would be clutter on every step of every tour. First contact disproved
            that: a reader on step 4 of a normal run looked for it and it was not
            there. An affordance that appears only in a rare state is one nobody
            learns, and "from the top" is not the same request as three presses of
            Back — Back retraces, this restarts.

            Beside the COUNTER rather than in the button row: it is about position,
            which is what the counter states, and the action row already carries
            Skip / Back / Next. Hidden at index 0, where it would mean nothing.
          */}
          {index > 0 && (
            <ButtonBase
              onClick={() => setIndex(0)}
              sx={{
                borderRadius: 1,
                px: 1,
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.text.primary },
                '&:focus-visible': {
                  boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
                },
              }}
            >
              <Typography variant="body4" component="span">
                Start over
              </Typography>
            </ButtonBase>
          )}
        </Stack>
        <IconButton aria-label="Close tour" size="small" onClick={doExit} sx={{ mt: -1, mr: -2 }}>
          <XIcon strokeWidth={1} size={16} />
        </IconButton>
      </Stack>

      {/*
        `pb: 5` (20px), up from 2 (8px) — the gap between the body copy and the button row.
 
        At 8px the description and the controls read as one block, so the eye arrived at "Next"
        while still reading the sentence. A tour card is a read-then-act unit and the pause between
        those two halves has to be visible; the buttons keep their own padding, so this is the only
        gap that separates prose from controls.
 
        Body copy is 15/1.6, up from `body3` (14/1.5). 14 is the app's LABEL size, and this is the
        one place in the product where a paragraph is the entire point of the surface — it sits in a
        ~340px card that a person is expected to actually read, not scan. 15 rather than 16 because
        the card is narrow: at 16 the sentence takes an extra line and the card grows past the
        element it is pointing at.
      */}
      <Stack sx={{ px: 4, pt: 3, pb: 5 }} spacing={2}>
        {/*
          `pt: 3` (12px) under the "N of M" counter, which previously sat flush against the title.
 
          12px is not arbitrary: the counter is to this card's title exactly what a breadcrumb is to
          a page title — a small locating label directly above the heading it belongs to — and §39
          fixes that gap at 12px app-wide. Same relationship, same number, so the card reads in the
          app's rhythm rather than its own.
        */}
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {step.title}
        </Typography>
        {/* lineHeight 1.6 — descriptive copy reads dense at the theme default (§15). */}
        <Typography color="text.secondary" sx={{ fontSize: 15, lineHeight: 1.6 }}>
          {step.body}
        </Typography>
      </Stack>

      {/* Branch hub — the author's final step hands off to a deeper tour. Gated on
          `isLastContent`, not `isLast` — see that constant's comment. */}
      {isLastContent && step.branches?.length ? (
        <Stack spacing={2} sx={{ px: 4, pb: 2 }}>
          {step.branches.map(b => (
            <Button
              key={b.tourId}
              variant="secondary"
              size="small"
              onClick={() => onBranch?.(b.tourId)}
              endIcon={<ArrowRightIcon strokeWidth={1} size={14} />}
              sx={{ justifyContent: 'space-between' }}
            >
              {b.label}
            </Button>
          ))}
        </Stack>
      ) : null}

      {/* The spotlight hint is copy, not a control, so it gets its own line
          instead of a slot in the button row. In the row it had to share 328px
          of card with Skip tour, Back and the advance button, and on a step that
          has all three — any terminal awaitAction step, e.g. the last step of
          certificates-basics — it ended up wedged between Back and Done with no
          air on either side. On its own line it has the full card width whatever
          combination of buttons the step renders.

          Shown only when a control actually resolved: with no anchor the card is
          a centered fallback with no ring, and the hint would be pointing at
          nothing. */}
      {step.awaitAction && !step.awaitAction.action && isAwait ? (
        <Typography
          variant="body4"
          color="text.secondary"
          sx={{ px: 4, pt: 1, pb: 1, display: { xs: 'none', sm: 'block' } }}
        >
          Or use the highlighted control
        </Typography>
      ) : null}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        sx={{ px: 4, pb: 4, pt: 1 }}
      >
        <Button variant="text" size="small" onClick={doSkip} sx={{ px: 0 }}>
          Skip tour
        </Button>

        <Stack direction="row" alignItems="center" spacing={3}>
          {index > 0 && (
            <Button
              variant="secondary"
              size="small"
              onClick={goBack}
              startIcon={<ArrowLeftIcon strokeWidth={1} size={14} />}
            >
              Back
            </Button>
          )}

          {step.awaitAction?.action ? (
            // Interactive step that can be performed FOR the user — the button
            // runs the action and advances.
            <Button size="small" onClick={doAwaitAction}>
              {step.awaitAction.buttonLabel ?? 'Show me'}
            </Button>
          ) : step.awaitAction ? (
            // Pure "click the real control" step. Clicking the spotlighted
            // element is the preferred path — it does the real thing — but that
            // control can be disabled or off-screen, which would TRAP the user.
            // So always offer a plain advance as well: a tour must never dead-end.
            // The hint that a real control is highlighted renders above this
            // row, not inside it — see the comment on it below the card body.
            <Button
              variant="secondary"
              size="small"
              onClick={goNext}
              endIcon={isLast ? undefined : <ArrowRightIcon strokeWidth={1} size={14} />}
            >
              {step.awaitAction.buttonLabel ?? (isLast ? 'Done' : 'Next')}
            </Button>
          ) : isLastContent && step.cta ? (
            // Gated on `isLastContent`, not `isLast` — see that constant's comment.
            // `doCta` ends the tour directly, so this deliberately REPLACES the
            // Next button rather than sitting beside it: the outro never renders
            // for a tour that ends on a cta, which is the point of using one.
            <Button size="small" onClick={doCta}>
              {step.cta.label}
            </Button>
          ) : (
            <Button
              variant={isLast ? 'primary' : 'secondary'}
              size="small"
              onClick={goNext}
              endIcon={isLast ? undefined : <ArrowRightIcon strokeWidth={1} size={14} />}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>,
    document.body
  )

  return (
    <>
      {backgroundLayer}
      {cardLayer}
    </>
  )
}
